import { z } from 'zod';

/**
 * QD-1 Prerequisite #2 — Required Scenario Policy.
 *
 * Authoritative REQUIRED means ONLY explicit membership in an immutable
 * policy version scoped by (projectId, environment).
 *
 * Advisory non-authority (must never create membership):
 * priority, risk, tags, Risk Selection bucket='required', gates,
 * Release Confidence, suites, requirement links, AI.
 *
 * Build identity stays on test_runs — never duplicated onto membership.
 */

/** Environment axis matches test_runs.environment (free-text, fail-closed). */
export const RequiredScenarioPolicyEnvironmentSchema = z.string().trim().min(1).max(200);
export type RequiredScenarioPolicyEnvironment = z.infer<typeof RequiredScenarioPolicyEnvironmentSchema>;

export const REQUIRED_SCENARIO_POLICY_RESOLVE_STATUSES = [
  'policy_missing',
  'policy_resolved',
  'policy_conflict',
  'policy_invalid',
] as const;
export type RequiredScenarioPolicyResolveStatus = (typeof REQUIRED_SCENARIO_POLICY_RESOLVE_STATUSES)[number];

export const REQUIRED_SCENARIO_MEMBERSHIP_STATES = [
  'required',
  'not_member',
  'policy_missing',
  'scenario_unavailable',
] as const;
export type RequiredScenarioMembershipState = (typeof REQUIRED_SCENARIO_MEMBERSHIP_STATES)[number];

export const RequiredScenarioPolicyMemberSchema = z.object({
  scenarioId: z.string().uuid(),
  /** Current live scenario status when the row still exists; null if hard-gone (should not happen with RESTRICT). */
  scenarioStatus: z.string().nullable(),
  /** Current live content version; distinct from policy version. */
  scenarioContentVersion: z.number().int().positive().nullable(),
  scenarioTitle: z.string().nullable(),
});
export type RequiredScenarioPolicyMember = z.infer<typeof RequiredScenarioPolicyMemberSchema>;

export const RequiredScenarioPolicyVersionSchema = z.object({
  projectId: z.string().uuid(),
  environment: RequiredScenarioPolicyEnvironmentSchema,
  policyVersion: z.number().int().positive(),
  createdAt: z.string(),
  createdBy: z.string().uuid().nullable(),
  members: z.array(RequiredScenarioPolicyMemberSchema),
});
export type RequiredScenarioPolicyVersion = z.infer<typeof RequiredScenarioPolicyVersionSchema>;

export const PublishRequiredScenarioPolicyRequestSchema = z.object({
  environment: RequiredScenarioPolicyEnvironmentSchema,
  /** Explicit required scenario IDs. Duplicates in the request fail closed. */
  scenarioIds: z.array(z.string().uuid()).max(2_000),
});
export type PublishRequiredScenarioPolicyRequest = z.infer<typeof PublishRequiredScenarioPolicyRequestSchema>;

export const PublishRequiredScenarioPolicyResponseSchema = RequiredScenarioPolicyVersionSchema;
export type PublishRequiredScenarioPolicyResponse = z.infer<typeof PublishRequiredScenarioPolicyResponseSchema>;

export const ResolveRequiredScenarioPolicyQuerySchema = z.object({
  environment: RequiredScenarioPolicyEnvironmentSchema,
  /** Omit = active/effective version. Provide to reconstruct a historical version. */
  policyVersion: z.coerce.number().int().positive().optional(),
});
export type ResolveRequiredScenarioPolicyQuery = z.infer<typeof ResolveRequiredScenarioPolicyQuerySchema>;

export const ResolveRequiredScenarioPolicyResponseSchema = z.object({
  status: z.enum(REQUIRED_SCENARIO_POLICY_RESOLVE_STATUSES),
  projectId: z.string().uuid(),
  environment: z.string(),
  /**
   * Present only when status === policy_resolved.
   * Empty members[] means explicit empty required set — NOT policy_missing.
   */
  policy: RequiredScenarioPolicyVersionSchema.nullable(),
});
export type ResolveRequiredScenarioPolicyResponse = z.infer<typeof ResolveRequiredScenarioPolicyResponseSchema>;

export const RequiredScenarioMembershipQuerySchema = z.object({
  environment: RequiredScenarioPolicyEnvironmentSchema,
  scenarioId: z.string().uuid(),
  policyVersion: z.coerce.number().int().positive().optional(),
});
export type RequiredScenarioMembershipQuery = z.infer<typeof RequiredScenarioMembershipQuerySchema>;

export const RequiredScenarioMembershipResponseSchema = z.object({
  state: z.enum(REQUIRED_SCENARIO_MEMBERSHIP_STATES),
  projectId: z.string().uuid(),
  environment: z.string(),
  scenarioId: z.string().uuid(),
  policyVersion: z.number().int().positive().nullable(),
  scenarioStatus: z.string().nullable(),
  scenarioContentVersion: z.number().int().positive().nullable(),
});
export type RequiredScenarioMembershipResponse = z.infer<typeof RequiredScenarioMembershipResponseSchema>;

/**
 * Pure helper for later QD-1: exact required scenario IDs from a resolved policy.
 * Returns null when policy is missing/invalid/conflicted — never invents [].
 */
export function requiredScenarioIdsFromResolved(resolved: ResolveRequiredScenarioPolicyResponse): string[] | null {
  if (resolved.status !== 'policy_resolved' || !resolved.policy) return null;
  return resolved.policy.members.map((m) => m.scenarioId);
}
